import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import i18n from 'i18n-js';
import useWidth from '../scanToCartScanning/useWidth';

const DuplicatePickWarningModal = ({
  visible,
  onClose,
  onContinue,
  onReport,
  onSilenceNotifications
}) => {
  const windowWidth = useWidth();

  if (!visible) return null;

  const GradientButton = ({text, onPress, testID, buttonStyle, textStyle}) => (
    <TouchableOpacity testID={testID} onPress={onPress}>
      <LinearGradient
        locations={[0, 0.7, 1]}
        start={{x: 0, y: 0}}
        end={{x: 0, y: 1}}
        colors={['#ffb636', '#ffb636', '#d47d02']}
        style={[styles.actionButton, buttonStyle]}>
        <Text style={[styles.buttonText, textStyle]}>{text}</Text>
      </LinearGradient>
    </TouchableOpacity>
  );

  const isMobile = windowWidth < 900;

  return (
    <View testID="duplicatePickWarningModal" style={[styles.responseView, isMobile && styles.responseViewMobile]}>
      <LinearGradient
        locations={[0, 1]}
        start={{x: 0, y: 1}}
        end={{x: 0, y: 0}}
        colors={['#d47d02', '#ffb636']}
        style={[
          styles.promptContainer,
          isMobile ? {maxWidth: '90%', width: '100%'} : {maxWidth: 700, width: '100%'}
        ]}>


        {!isMobile && (
          <View style={styles.cancelIcon}>
            <TouchableOpacity testID="CancelIcon" onPress={onClose}>
              <i
                className="icon-cancel"
                style={{fontSize: 26, color: '#1C1C1C'}}></i>
            </TouchableOpacity>
          </View>
        )}

        {/* Header with Icons and Title */}
        <View style={[styles.headerContainer, isMobile && styles.headerContainerMobile]}>
          <View style={styles.iconContainer}>
            <Text style={[styles.warningIcon, isMobile && styles.warningIconMobile]}>⚠</Text>
          </View>
          <View style={styles.titleContainer}>
            <Text style={[
              styles.titleText,
              isMobile && styles.titleTextSmall,
              isMobile && styles.titleTextMobile
            ]}>
              {i18n.t('duplicatePickWarning.title')}
            </Text>
          </View>
          <View style={styles.iconContainer}>
            <Text style={[styles.warningIcon, isMobile && styles.warningIconMobile]}>⚠</Text>
          </View>
        </View>

        {/* Warning Message */}
        <View style={[styles.messageContainer, isMobile && styles.messageContainerMobile]}>
          <Text style={[styles.messageText, isMobile && styles.messageTextMobile]}>
            {i18n.t('duplicatePickWarning.message')}
          </Text>
        </View>

        {/* Action Buttons */}
        <View style={[
          styles.buttonContainer,
          {flexDirection: isMobile ? 'column' : 'row'},
          isMobile && styles.buttonContainerMobile
        ]}>
          <GradientButton
            testID="continueButton"
            text={i18n.t('duplicatePickWarning.continueButton')}
            onPress={onContinue}
            textStyle={isMobile && styles.buttonTextMobile}
          />
          <GradientButton
            testID="reportButton"
            buttonStyle={styles.reportButton}
            textStyle={[styles.reportButtonText, isMobile && styles.buttonTextMobile]}
            text={i18n.t('duplicatePickWarning.reportButton')}
            onPress={onReport}
          />
        </View>

        {/* Dark Section - Secondary Information */}
        <View style={[styles.darkSection, isMobile && styles.darkSectionMobile]}>
          <Text style={[styles.questionText, isMobile && styles.questionTextMobile]}>
            {i18n.t('duplicatePickWarning.question')}
          </Text>
          <TouchableOpacity onPress={onSilenceNotifications} style={styles.linkContainer}>
            <Text style={[styles.linkText, isMobile && styles.linkTextMobile]}>
              {i18n.t('duplicatePickWarning.instructionPrefix')}{' '}
              {i18n.t('duplicatePickWarning.instructionLink')}
              {' '}{i18n.t('duplicatePickWarning.instructionSuffix')}
            </Text>
          </TouchableOpacity>
        </View>

        {isMobile && (
          <TouchableOpacity
            testID="mobileCloseButton"
            onPress={onClose}
            style={styles.mobileCloseButton}>
            <Text style={styles.mobileCloseButtonText}>
              {i18n.t('duplicatePickWarning.cancelButton')}
            </Text>
          </TouchableOpacity>
        )}
      </LinearGradient>
    </View>
  );
};

const styles = StyleSheet.create({
  responseView: {
    flex: 1,
    justifyContent: 'flex-start',
    alignItems: 'center',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    zIndex: 1000,
    width: '100%',
    height: '100%',
    padding: 40
  },
  promptContainer: {
    backgroundColor: '#d3dfea',
    paddingHorizontal: 20,
    paddingVertical: 15,
    width: '90%',
    marginTop: 0,
    borderColor: '#e8971a',
    borderWidth: 3,
    borderRadius: 10,
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    position: 'relative'
  },
  cancelIcon: {
    display: 'flex',
    alignSelf: 'flex-end'
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 10,
    marginBottom: 15,
  },
  iconContainer: {
    marginHorizontal: 8,
  },
  warningIcon: {
    fontSize: 24,
    color: '#cc0000',
  },
  titleContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 1,
    flex: 1,
  },
  messageContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  messageText: {
    fontFamily: 'Poppins_300Light',
    fontSize: 16,
    textAlign: 'center',
    color: '#000',
    lineHeight: 22,
  },
  buttonContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    gap: 15,
    marginBottom: 20,
  },
  actionButton: {
    borderWidth: 1,
    borderColor: '#1C1C1C',
    borderRadius: 20,
    paddingVertical: 8,
    paddingHorizontal: 16,
    flex: 1,
    minWidth: 0,
    alignItems: 'center',
    justifyContent: 'center'
  },
  buttonText: {
    color: '#1C1C1C',
    fontFamily: 'Poppins_300Light',
    fontSize: 16,
    textShadowColor: 'rgba(0, 0, 0, 0.25)',
    textShadowOffset: {width: 1, height: 1},
    textShadowRadius: 2,
    fontWeight: '500'
  },
  darkSection: {
    backgroundColor: '#000000',
    paddingHorizontal: 20,
    paddingVertical: 20,
    marginTop: 10,
    marginBottom: 10,
    marginLeft: -20,
    marginRight: -20,
    alignSelf: 'stretch'
  },
  questionText: {
    fontSize: 16,
    color: '#ffffff',
    textAlign: 'center',
    marginBottom: 12,
    fontFamily: 'Poppins_300Light',
  },
  linkContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  linkText: {
    fontSize: 14,
    color: '#ffb636',
    textDecorationLine: 'underline',
    textAlign: 'center',
    fontFamily: 'Poppins_300Light',
  },
  reportButton: {
    borderWidth: 2,
    borderColor: '#1C1C1C',
    paddingVertical: 7,
    paddingHorizontal: 15,
  },
  reportButtonText: {
    fontWeight: 'bold',
  },
  titleText: {
    fontWeight: 'bolder',
    fontSize: 20,
    color: '#000000',
    textAlign: 'center',
    fontFamily: 'Poppins_600SemiBold',
    fontSize: 20,
    textAlign: 'center',
    color: '#000',
    fontWeight: '500',
  },
  titleTextSmall: {
    flexWrap: 'wrap',
    maxWidth: '100%',
    flexShrink: 1,
  },
  // Mobile-specific overrides
  responseViewMobile: {
    padding: 10,
  },
  headerContainerMobile: {
    marginTop: 4,
    marginBottom: 8,
  },
  warningIconMobile: {
    fontSize: 18,
  },
  titleTextMobile: {
    fontSize: 15,
  },
  messageContainerMobile: {
    marginBottom: 10,
  },
  messageTextMobile: {
    fontSize: 13,
    lineHeight: 18,
  },
  buttonContainerMobile: {
    gap: 8,
    marginBottom: 12,
  },
  buttonTextMobile: {
    fontSize: 13,
  },
  darkSectionMobile: {
    paddingHorizontal: 12,
    paddingVertical: 12,
    marginTop: 6,
    marginBottom: 6,
  },
  questionTextMobile: {
    fontSize: 13,
    marginBottom: 8,
  },
  linkTextMobile: {
    fontSize: 12,
  },
  mobileCloseButton: {
    alignSelf: 'center',
    marginTop: 10,
    marginBottom: 5,
    paddingVertical: 8,
    paddingHorizontal: 30,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#1C1C1C',
    backgroundColor: 'rgba(255,255,255,0.2)',
  },
  mobileCloseButtonText: {
    color: '#1C1C1C',
    fontFamily: 'Poppins_300Light',
    fontSize: 14,
    fontWeight: '500',
  },
});

export default DuplicatePickWarningModal;
